import { Id, toast } from "react-toastify";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { fetchItem, postItem } from "../../queries/queries-helper";
import ResponseApi from "../../models/interfaces/response-api";
import { JobAdapter, UnifiedJobStatus } from "../../models/interfaces/dtos/job";
import { useOptionalProjectProcessContext } from "@/services/context/project-process/use-project-process-context";
import type { ProductionProcessOptions } from "@/services/context/project-process/project-process-runner";
import pollJob from "./poll-job";

export interface JobStatusMessage {
    pending?: string;
    success?: string;
    error?: string;
}

const DEFAULT_MESSAGES: Required<JobStatusMessage> = {
    pending: "Processus en cours...",
    success: "Travail terminé.",
    error: "Une erreur est survenue.",
};

interface UseJobAsyncOptions<TJob> {
    jobUrl: () => string;
    jobData?: unknown | (() => unknown);
    // Requis avec getExistingJobUrl pour vérifier que le job retrouvé correspond au traitement.
    processName?: string;
    productionOptions?: ProductionProcessOptions;
    getExistingJobUrl?: string;
    getStatusUrl: (jobId: string) => string;
    adapter: JobAdapter<TJob>;
    onJobDone?: (jobId: string, toastId: Id) => Promise<string | void> | string | void;
    jobFinished?: (succeeded: boolean, isExistingJob: boolean) => Promise<void> | void;
    onError?: () => void;
    messages?: JobStatusMessage;
}

const useJobAsync = <TJob,>(options: UseJobAsyncOptions<TJob>) => {
    const processContext = useOptionalProjectProcessContext();
    const beginProcess = processContext?.beginProcess;
    const runProductionProcess = processContext?.runProductionProcess;
    const optionsRef = useRef(options);
    const isMounted = useRef(true);
    const isRunning = useRef(false);
    const [isLoading, setIsLoading] = useState(false);

    useLayoutEffect(() => {
        optionsRef.current = options;
    }, [options]);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    const launchJob = useCallback(async (overrides?: Partial<UseJobAsyncOptions<TJob>>): Promise<boolean> => {
        if (isRunning.current || !isMounted.current) return false;
        isRunning.current = true;
        setIsLoading(true);
        const finishProcess = beginProcess?.();
        // Figer les paramètres et callbacks de ce lancement, même après un nouveau rendu.
        const run = { ...optionsRef.current, ...overrides };
        const messages = {
            pending: run.messages?.pending ?? DEFAULT_MESSAGES.pending,
            success: run.messages?.success ?? DEFAULT_MESSAGES.success,
            error: run.messages?.error ?? DEFAULT_MESSAGES.error,
        };
        let toastId: Id | undefined;
        let isExistingJob = false;

        try {
            const jobUrl = run.jobUrl();
            const jobData = typeof run.jobData === "function" ? run.jobData() : run.jobData;
            if (run.processName && run.getExistingJobUrl && runProductionProcess) {
                const result = await runProductionProcess(run.processName, jobUrl, run.productionOptions);
                // La suite métier appartient au gestionnaire global ; ces callbacks concernent la page affichée.
                if (isMounted.current) {
                    if (result.succeeded) {
                        await run.onJobDone?.(result.jobId, result.toastId);
                        if (isMounted.current) await run.jobFinished?.(true, result.isExistingJob);
                    } else {
                        await run.jobFinished?.(false, false);
                        if (isMounted.current) run.onError?.();
                    }
                }
                return result.succeeded;
            }
            toastId = toast.loading(messages.pending);
            let jobId: string | undefined;

            if (run.getExistingJobUrl) {
                // Une erreur de vérification ne doit pas provoquer un nouveau lancement.
                const existing = await fetchItem<TJob>(run.getExistingJobUrl);
                if (!isMounted.current) return false;
                if (existing && [UnifiedJobStatus.Pending, UnifiedJobStatus.Processing].includes(run.adapter.getStatus(existing))) {
                    if (!run.processName || run.adapter.getName?.(existing) !== run.processName) {
                        throw new Error("Un autre processus est en cours sur ce projet. Attendez sa fin avant de lancer ce traitement.");
                    }
                    jobId = run.adapter.getId(existing);
                    isExistingJob = true;
                }
            }

            if (!jobId) {
                const response = await postItem<ResponseApi<TJob>>(jobUrl, jobData);
                if (!response?.success) throw new Error(response?.errorMessages?.[0] ?? messages.error);
                jobId = run.adapter.getId(response.entity);
            }

            const status = await pollJob(
                () => fetchItem<TJob>(run.getStatusUrl(jobId!)),
                run.adapter.getStatus,
                () => isMounted.current,
            );
            if (!status) return false;

            const succeeded = run.adapter.getStatus(status) === UnifiedJobStatus.Success;
            if (succeeded) {
                const successMessage = await run.onJobDone?.(jobId, toastId);
                if (!isMounted.current) return false;
                await run.jobFinished?.(true, isExistingJob);
                if (!isMounted.current) return false;
                toast.update(toastId, {
                    render: successMessage ?? messages.success, type: "success", isLoading: false,
                    autoClose: 5000, closeButton: true,
                });
                return true;
            }

            await run.jobFinished?.(false, isExistingJob);
            throw new Error(messages.error);
        } catch (error) {
            if (isMounted.current) {
                const notification = {
                    render: error instanceof Error ? error.message : messages.error,
                    type: "error" as const, isLoading: false, autoClose: false as const, closeButton: true,
                };
                if (toastId !== undefined) toast.update(toastId, notification);
                else toast.error(notification.render);
                run.onError?.();
            }
            return false;
        } finally {
            if (!isMounted.current && toastId !== undefined) toast.dismiss(toastId);
            finishProcess?.();
            isRunning.current = false;
            if (isMounted.current) setIsLoading(false);
        }
    }, [beginProcess, runProductionProcess]);

    return { launchJob, isLoading };
};

export default useJobAsync;
